import { QueryTableApiView } from "@/components/query-table/views/QueryTableApiView";
import { QueryTableSelectedOnlyView } from "@/components/query-table/views/QueryTableSelectedOnlyView";
import { useAutoSyncedRowSelection } from "@/components/query-table/hooks/useAutoSyncedRowSelection";
import { dataTableOnlySelectedViewModes } from "@/types/dataTableSettings";
import {
  QueryTableProps,
  useMergedListFeatures,
} from "@/components/query-table/QueryTable.shared";
import { QueryTableContent } from "./components/QueryTableContent";
import { QueryTableDirtyRowsStatus } from "./components/QueryTableDirtyRowsStatus";
import { QueryTableHeader } from "./components/QueryTableHeader";
import { QueryTableMenubar } from "./components/QueryTableMenubar";
import { QueryTableSelectedRowsStatus } from "./components/QueryTableSelectedRowsStatus";
import { QueryTableStatus } from "./components/QueryTableStatus";
import { useMemo } from "react";
import { useQueryTableStore } from "./stores/onlySelectedViewStore";

export type { QueryTableProps };
export type ListProps<T> = QueryTableProps<T>;

export const QueryTable = <T extends { id: string }>({
  name,
  tableFeatures,
  ...rest
}: QueryTableProps<T>) => {
  const tableFeaturesMerged = useMergedListFeatures(tableFeatures);
  const isOnlySelectedFeatureEnabled = Boolean(
    tableFeaturesMerged.enableOnlySelectedFilter &&
      tableFeaturesMerged.enableRowSelection,
  );
  const mode = useQueryTableStore((s) => s.modesByTable[name]);
  const showOnlySelectedView =
    isOnlySelectedFeatureEnabled &&
    mode === dataTableOnlySelectedViewModes.selected;

  const selectedOnlyTableFeatures = useMemo(
    () => ({
      ...tableFeaturesMerged,
      enableFilters: false,
      enableSorting: false,
    }),
    [tableFeaturesMerged],
  );
  const {
    rowSelection: autoRowSelection,
    setRowSelection: autoSetRowSelection,
  } = useAutoSyncedRowSelection({
    enabled: Boolean(tableFeaturesMerged.enableRowSelection),
  });
  const listProps = {
    ...rest,
    name,
    tableFeatures,
    rowSelection: rest.rowSelection ?? autoRowSelection,
    setRowSelection: rest.setRowSelection ?? autoSetRowSelection,
  };

  if (showOnlySelectedView) {
    return (
      <QueryTableSelectedOnlyView<T>
        listProps={listProps}
        tableFeaturesMerged={selectedOnlyTableFeatures}
      />
    );
  }

  return (
    <QueryTableApiView<T>
      listProps={listProps}
      tableFeaturesMerged={tableFeaturesMerged}
    />
  );
};

QueryTable.Header = QueryTableHeader;
QueryTable.Menubar = QueryTableMenubar;
QueryTable.Content = QueryTableContent;
QueryTable.Status = QueryTableStatus;
QueryTable.DirtyRowsStatus = QueryTableDirtyRowsStatus;
QueryTable.SelectedRowsStatus = QueryTableSelectedRowsStatus;

QueryTable.displayName = "QueryTable";
